/**
 * Portfolio Tab Component
 *
 * Displays portfolio positions, trades, and summary for authenticated users.
 * Auto-refreshes when screen comes into focus (e.g., after resolving instrument or adding comments).
 *
 * T110-T111: Added summary tab with pie charts
 * NOW THEME-AWARE and i18n SUPPORTED
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  SafeAreaView,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { getPositions, getTradesPaginated } from '../../services/portfolioApi';
import { PositionTable } from './PositionTable';
import { TradeList } from './TradeList';
import { ImportButton } from './ImportButton';
import { PortfolioActions } from './PortfolioActions';
import { WatchlistScreen } from '../../screens/watchlist/WatchlistScreen';
import { PortfolioThesisForm } from './PortfolioThesisForm';
import { CheckBox } from '../ui/CheckBox';
import { useTheme } from '../../contexts/ThemeContext';
import { showError } from '../../utils/alert';
import type { Position, Trade } from '../../services/portfolioApi';
import type { PortfolioNavigatorParamList } from '../../navigation/PortfolioNavigator';

// Conditionally import SegmentedControlIOS only on native platforms
let SegmentedControlIOS: any = undefined;
if (Platform.OS === 'ios' && !(Platform as any).isWeb) {
  const ReactNative = require('react-native');
  SegmentedControlIOS = ReactNative.SegmentedControlIOS;
}

type PortfolioTabNavigationProp = StackNavigationProp<PortfolioNavigatorParamList, 'PortfolioHome'>;

type Tab = 'thesis' | 'positions' | 'watchlist' | 'trades'; // Watchlist sits between positions and trades

const PAGE_SIZE = 20;

export function PortfolioTab() {
  const navigation = useNavigation<PortfolioTabNavigationProp>();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const [activeTab, setActiveTab] = useState<Tab>('positions');
  const [positions, setPositions] = useState<Position[]>([]);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [triggerImport, setTriggerImport] = useState<'image' | null>(null);
  const [showZeroBalance, setShowZeroBalance] = useState(false);

  // Pagination state for trades
  const [tradesOffset, setTradesOffset] = useState(0);
  const [tradesHasMore, setTradesHasMore] = useState(false);
  const [tradesTotalCount, setTradesTotalCount] = useState(0);
  const [loadingMoreTrades, setLoadingMoreTrades] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [posData] = await Promise.all([getPositions()]);
      setPositions(posData);

      // Load first page of trades
      const tradesData = await getTradesPaginated(0, PAGE_SIZE);
      setTrades(tradesData.trades);
      setTradesOffset(tradesData.offset + tradesData.limit);
      setTradesHasMore(tradesData.has_more);
      setTradesTotalCount(tradesData.total_count);
    } catch (error: any) {
      console.error('Failed to load portfolio:', error?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [posData] = await Promise.all([getPositions()]);
      setPositions(posData);

      // Reload trades from first page
      const tradesData = await getTradesPaginated(0, PAGE_SIZE);
      setTrades(tradesData.trades);
      setTradesOffset(tradesData.offset + tradesData.limit);
      setTradesHasMore(tradesData.has_more);
      setTradesTotalCount(tradesData.total_count);
    } catch (error: any) {
      console.error('Failed to refresh portfolio:', error?.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const loadMoreTrades = useCallback(async () => {
    if (loadingMoreTrades || !tradesHasMore) return;

    setLoadingMoreTrades(true);
    try {
      const tradesData = await getTradesPaginated(tradesOffset, PAGE_SIZE);
      setTrades((prev) => [...prev, ...tradesData.trades]);
      setTradesOffset(tradesData.offset + tradesData.limit);
      setTradesHasMore(tradesData.has_more);
      setTradesTotalCount(tradesData.total_count);
    } catch (error: any) {
      console.error('Failed to load more trades:', error?.message);
    } finally {
      setLoadingMoreTrades(false);
    }
  }, [loadingMoreTrades, tradesHasMore, tradesOffset]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset triggerImport after it's been handled
  React.useEffect(() => {
    if (triggerImport) {
      // Small delay to ensure ImportButton receives the prop
      const timer = setTimeout(() => {
        setTriggerImport(null);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [triggerImport]);

  // Auto-refresh when screen comes into focus (e.g., returning from detail screens)
  useFocusEffect(
    useCallback(() => {
      loadData();
    }, [loadData])
  );

  const filteredPositions = useMemo(
    () => (showZeroBalance ? positions : positions.filter((p) => Number(p.quantity) !== 0)),
    [positions, showZeroBalance]
  );

  const handleTradePress = useCallback(
    (trade: Trade) => {
      // Navigate to position detail screen using trade's position_id
      if (trade.position_id) {
        navigation.navigate('PositionDetail', {
          positionId: trade.position_id,
        });
      } else {
        showError(
          t('errors.positionNotFound', { defaultValue: 'Position not found for this trade' })
        );
      }
    },
    [navigation, t]
  );

  // T072 [US4]: Handle edit button press - navigate to edit screen
  const handleEditPress = useCallback(
    (trade: Trade) => {
      navigation.navigate('AddTrade', { tradeId: trade.id });
    },
    [navigation]
  );

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>
            {t('portfolio.loadingPortfolio', { defaultValue: 'Loading portfolio...' })}
          </Text>
        </View>
      );
    }

    if (activeTab === 'thesis') {
      return <PortfolioThesisForm />;
    } else if (activeTab === 'positions') {
      return <PositionTable positions={filteredPositions} />;
    } else if (activeTab === 'trades') {
      return (
        <TradeList
          trades={trades}
          onTradePress={handleTradePress}
          onEditPress={handleEditPress}
          onLoadMore={loadMoreTrades}
          hasMore={tradesHasMore}
          loading={loadingMoreTrades}
          totalCount={tradesTotalCount}
        />
      );
    }
    return null;
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>
          {t('portfolio.portfolio', { defaultValue: 'Portfolio' })}
        </Text>
        <View style={styles.headerActions}>
          <TouchableOpacity
            testID="refresh-portfolio-button"
            style={styles.headerButton}
            onPress={handleRefresh}
            disabled={refreshing}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            {refreshing ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Ionicons name="refresh-outline" size={24} color={theme.colors.text} />
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.headerButton}
            onPress={() => setShowActionsMenu(true)}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Ionicons name="add-circle-outline" size={24} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>
      </View>
      {SegmentedControlIOS ? (
        <SegmentedControlIOS
          values={[
            t('portfolio.thesis'),
            t('portfolio.positions'),
            t('watchlist.title'),
            t('portfolio.trades'),
          ]}
          selectedIndex={
            activeTab === 'thesis'
              ? 0
              : activeTab === 'positions'
                ? 1
                : activeTab === 'watchlist'
                  ? 2
                  : 3
          }
          style={styles.segmentedControl}
          onChange={(event: any) => {
            const index = event.nativeEvent.selectedSegmentIndex;
            setActiveTab(
              index === 0
                ? 'thesis'
                : index === 1
                  ? 'positions'
                  : index === 2
                    ? 'watchlist'
                    : 'trades'
            );
          }}
        />
      ) : (
        <View style={styles.androidTabs}>
          <TouchableOpacity
            style={[styles.androidTab, activeTab === 'thesis' && styles.androidTabActive]}
            onPress={() => setActiveTab('thesis')}
            activeOpacity={0.7}
          >
            <Text
              style={[styles.androidTabText, activeTab === 'thesis' && styles.androidTabTextActive]}
            >
              {t('portfolio.thesis')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.androidTab, activeTab === 'positions' && styles.androidTabActive]}
            onPress={() => setActiveTab('positions')}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.androidTabText,
                activeTab === 'positions' && styles.androidTabTextActive,
              ]}
            >
              {t('portfolio.positions')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.androidTab, activeTab === 'watchlist' && styles.androidTabActive]}
            onPress={() => setActiveTab('watchlist')}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.androidTabText,
                activeTab === 'watchlist' && styles.androidTabTextActive,
              ]}
            >
              {t('watchlist.title')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.androidTab, activeTab === 'trades' && styles.androidTabActive]}
            onPress={() => setActiveTab('trades')}
            activeOpacity={0.7}
          >
            <Text
              style={[styles.androidTabText, activeTab === 'trades' && styles.androidTabTextActive]}
            >
              {t('portfolio.trades')}
            </Text>
          </TouchableOpacity>
        </View>
      )}
      {activeTab === 'positions' && (
        <TouchableOpacity
          style={styles.filterBar}
          activeOpacity={0.7}
          onPress={() => setShowZeroBalance(!showZeroBalance)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: showZeroBalance }}
          accessibilityLabel={t('portfolio.showZeroBalance', {
            defaultValue: 'Show zero-balance positions',
          })}
        >
          <View pointerEvents="none">
            <CheckBox
              value={showZeroBalance}
              onValueChange={() => undefined}
              testID="show-zero-balance-checkbox"
            />
          </View>
          <Text style={[styles.filterLabel, { color: theme.colors.textSecondary }]}>
            {t('portfolio.showZeroBalance', { defaultValue: 'Show zero-balance positions' })}
          </Text>
        </TouchableOpacity>
      )}
      {activeTab === 'watchlist' ? (
        // WatchlistScreen owns its own FlatList — render it directly, NOT inside
        // the ScrollView below, to avoid a nested virtualized-list scroll bug.
        <WatchlistScreen />
      ) : (
        <ScrollView
          style={styles.content}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={theme.colors.primary}
            />
          }
        >
          {renderContent()}
        </ScrollView>
      )}
      <PortfolioActions
        visible={showActionsMenu}
        onClose={() => setShowActionsMenu(false)}
        onAddPosition={() => navigation.navigate({ name: 'AddPosition', params: {} })}
        onAddCash={() => navigation.navigate('AddCash')}
        onAddTrade={() => navigation.navigate({ name: 'AddTrade', params: {} })}
        onAddCorporateAction={() => navigation.navigate({ name: 'CorporateAction', params: {} })}
        onSimulate={() => navigation.navigate('Simulate')}
        onImportPhoto={() => setTriggerImport('image')}
      />
      {/* Programmatic trigger only: overflow from this zero-sized wrapper must
          never participate in hit testing or it can open the import modal. */}
      <View style={styles.hiddenImportButton} pointerEvents="none">
        <ImportButton onUploadComplete={handleRefresh} triggerImport={triggerImport} />
      </View>
    </SafeAreaView>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      backgroundColor: theme.colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
    },
    headerTitle: {
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    headerActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 16,
    },
    headerButton: {
      padding: 4,
    },
    segmentedControl: {
      margin: 16,
    },
    androidTabs: {
      flexDirection: 'row',
      margin: 16,
      backgroundColor: theme.colors.backgroundElevated,
      borderRadius: theme.radii.md,
      flexWrap: 'wrap',
    },
    androidTab: {
      flex: 1,
      paddingVertical: 12,
      alignItems: 'center',
      borderRadius: theme.radii.md,
    },
    androidTabActive: {
      backgroundColor: theme.colors.primary,
    },
    androidTabText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
    },
    androidTabTextActive: {
      color: theme.colors.textInverse,
    },
    content: {
      flex: 1,
    },
    filterBar: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 8,
      gap: 8,
    },
    filterLabel: {
      fontSize: theme.typography.fontSizeSm,
    },
    centerContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 48,
    },
    loadingText: {
      marginTop: 12,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
    hiddenImportButton: {
      position: 'absolute',
      width: 0,
      height: 0,
      opacity: 0,
    },
  });
}
